import { styled } from '@mui/material';
import APIRoleBasedRouting from 'app/APIRoleBasedRouting';
import { shouldShowLogoOnlyHeader, shouldShowNavigation, shouldUseSidebarLayout } from 'app/layout';
import ProviderWrapper from 'app/ProviderWrapper';
import axios from 'axios';
import InstructorHugoChat from 'components/Chat/instructor-hugo';
import LogoOnlyHeader from 'components/Header/LogoOnlyHeader';
import Navigation from 'components/Navigation';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { captureApiFailure, captureApiSuccess } from 'services/activity';
import { Box, Loading, Stack } from 'shell/components';
import { expireAuthSession } from 'utils/auth';

axios.defaults.baseURL = process.env.REACT_APP_API_ENDPOINT;
axios.interceptors.response.use(
    (response) => {
        captureApiSuccess(response);
        return response.data;
    },
    (error) => {
        const { response = {} } = error;
        captureApiFailure(error);
        if (response.status === 401) {
            expireAuthSession();
        }
        return Promise.reject(response);
    }
);

const noHeaderRoutes = [
    '/api-launch',
    '/components',
    '/course-set-up',
    '/lti/deep-link-picker',
    '/lti-incomplete',
    '/lti-launch',
    '/profile-setup',
];
const noInstructorHugoRoutes = ['/settings'];

const pathnameStartsWithRoute = (pathname, route) => pathname === route || pathname.startsWith(`${route}/`);

const AppHeader = () => {
    const { auth } = useAuth();
    const location = useLocation();

    const showLogoOnlyHeader = shouldShowLogoOnlyHeader(auth, location.pathname);

    if (!showLogoOnlyHeader && !shouldShowNavigation(auth, location.pathname)) return null;

    return showLogoOnlyHeader ? <LogoOnlyHeader /> : <Navigation />;
};

const InstructorHugoAssistant = ({ onFullScreenChange }) => {
    const { auth } = useAuth();
    const location = useLocation();
    const isDisabledRoute = noInstructorHugoRoutes.some((route) => pathnameStartsWithRoute(location.pathname, route));

    if (!['instructor', 'ta'].includes(auth?.role) || isDisabledRoute) return null;

    return <InstructorHugoChat onFullScreenChange={onFullScreenChange} />;
};

const AppContent = () => {
    const { auth } = useAuth();
    const { device } = useViewport();
    const location = useLocation();
    const [isHugoFullScreen, setIsHugoFullScreen] = useState(false);

    const handleHugoFullScreenChange = useCallback((isFullScreen) => {
        setIsHugoFullScreen(isFullScreen);
    }, []);

    const renderRoute = () => {
        return <APIRoleBasedRouting />;
    };

    useEffect(() => {
        let observer;

        const updateTitle = () => {
            const h1Elements = document.querySelectorAll('h1');
            if (h1Elements.length === 1) {
                document.title = `${h1Elements[0].textContent} - TimelyGrader`;
            } else if (h1Elements.length > 1) {
                const titles = Array.from(h1Elements)
                    .slice(1)
                    .map((h1) => h1.textContent);
                document.title = `${titles.join(' & ')} - TimelyGrader`;
            } else {
                document.title = 'TimelyGrader';
            }
        };

        observer = new MutationObserver(() => {
            updateTitle();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        updateTitle();

        return () => {
            if (observer) observer.disconnect();
            document.title = 'TimelyGrader';
        };
    }, []);

    return (
        <PageWrapper direction={shouldUseSidebarLayout(auth, device, location.pathname) ? 'row' : 'column'}>
            <AppHeader />
            <BodyWrapper data-app-scroll-container isHugoFullScreen={isHugoFullScreen} tabIndex={-1}>
                {renderRoute()}
            </BodyWrapper>
            <InstructorHugoAssistant onFullScreenChange={handleHugoFullScreenChange} />
        </PageWrapper>
    );
};

const App = () => {
    const { loading } = useAuth();

    return loading ? (
        <Loading />
    ) : (
        <ProviderWrapper>
            <AppContent />
        </ProviderWrapper>
    );
};

const PageWrapper = styled(Stack)(() => {
    return {
        height: '100%',
        minWidth: 0,
        minHeight: 0,
    };
});

const BodyWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isHugoFullScreen',
})(({ theme, isHugoFullScreen }) => {
    return {
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        position: 'relative',
        overflow: isHugoFullScreen ? 'hidden' : 'auto',
        overscrollBehavior: isHugoFullScreen ? 'none' : undefined,
        background: theme.palette.ensightful.neutral[50],
    };
});

export default App;
